/**
 * Maps creator submission wizard state into the experience object shape consumed
 * by normalizeExperience() and SimpleModal.
 */

function labelsFor(options, slugs) {
  if (!Array.isArray(slugs) || !slugs.length || !Array.isArray(options)) return [];
  const map = Object.fromEntries(options.map((o) => [o.slug, o.label]));
  return slugs.map((s) => map[s]).filter(Boolean);
}

function parseLocationText(text) {
  const empty = {
    venueName: null,
    streetAddress: null,
    city: null,
    stateRegion: null,
    country: null,
  };
  try {
    const lines = (text || "")
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);
    if (lines.length < 3) return empty;

    const [venueLine, streetLine, cityStateLine] = lines;
    const cityStateMatch = cityStateLine.match(/^(.+?),\s*([A-Za-z]{2,})\s+(\d{5}(?:-\d{4})?)$/);
    if (cityStateMatch) {
      return {
        venueName: venueLine,
        streetAddress: streetLine,
        city: cityStateMatch[1],
        stateRegion: cityStateMatch[2],
        country: "US",
      };
    }
    const intlMatch = cityStateLine.match(/^(.+?),\s*(.+)$/);
    if (intlMatch) {
      return {
        venueName: venueLine,
        streetAddress: streetLine,
        city: intlMatch[1],
        stateRegion: null,
        country: intlMatch[2],
      };
    }
    return { ...empty, venueName: venueLine, streetAddress: streetLine, city: cityStateLine };
  } catch {
    return empty;
  }
}

const SUBMISSION_PLATFORM_TO_ONTOLOGY = {
  "meta-quest": "quest",
  "apple-vision-pro": "vision-pro",
  pico: "quest",
  "playstation-vr": "psvr",
  pcvr: "pcvr",
  "android-xr": "android",
  "mobile-ios": "ios",
  "mobile-android": "android",
  pc: "pcvr",
  mac: "browser",
  "web-browser": "browser",
  "in-venue-equipment": "lbe",
};

const PRICING_TO_PRICE_TYPE = {
  free: "free",
  freemium: "paid",
  paid: "paid",
  ticketed: "ticket",
  subscription: "paid",
  "included-with-admission": "ticket",
  "contact-for-pricing": "paid",
};

const LOCATION_MODEL_TO_VENUE_MODEL = {
  anywhere: "gps-anchored",
  "at-home": "at-home",
  "fixed-location": "specific-venue",
  "multiple-locations": "specific-venue",
};

const AVAILABILITY_MAP = {
  "available-now": { availabilityStatus: "available-now", availabilityType: "persistent" },
  "limited-run": { availabilityStatus: "limited-availability", availabilityType: "limited-run" },
  seasonal: { availabilityStatus: "coming-soon", availabilityType: "scheduled" },
  touring: { availabilityType: "touring" },
  "reservation-required": { availabilityStatus: "limited-availability", availabilityType: "scheduled" },
};

function inferAccessFromMethods(accessMethods) {
  const surfaces = new Set();
  const delivery = new Set();
  let isPhysical = false;

  for (const method of accessMethods || []) {
    if (method === "browser-web") {
      ["mobile-browser", "desktop-browser", "headset-browser"].forEach((s) => surfaces.add(s));
      delivery.add("webxr");
    }
    if (method === "downloadable-app") {
      ["mobile-app", "headset-app", "desktop-app"].forEach((s) => surfaces.add(s));
      delivery.add("native-app");
    }
    if (method === "in-person") {
      isPhysical = true;
    }
  }

  return {
    accessSurface: [...surfaces],
    deliveryTechnology: [...delivery],
    isPhysical,
  };
}

/**
 * @param {object} draft Wizard field values
 * @param {object} options Chip option arrays from ShareExperienceFlow
 */
export function buildSubmissionPreviewExperience(draft, options) {
  const {
    title,
    creatorDisplayName,
    heroPreviewUrl,
    preview,
    experienceDescription,
    primaryExperienceLink,
    formats = [],
    themes = [],
    accessMethods = [],
    locationModels = [],
    platforms = [],
    platformNotes,
    players = [],
    duration = [],
    pricingModels = [],
    priceDetails,
    availability = [],
    availabilityDetails,
    recommendedAudiences = [],
    supportedLanguages = [],
    contentNotes,
    locationText,
    requiresLocation,
    referenceImageUrls = [],
  } = draft;

  const formatLabels = labelsFor(options.FORMAT_OPTIONS, formats);
  const platformLabels = labelsFor(options.PLATFORM_OPTIONS, platforms);
  const playerLabels = labelsFor(options.PLAYER_OPTIONS, players);
  const durationLabels = labelsFor(options.DURATION_OPTIONS, duration);
  const audienceLabels = labelsFor(options.RECOMMENDED_AUDIENCE_OPTIONS, recommendedAudiences);
  const languageLabels = labelsFor(options.SUPPORTED_LANGUAGE_OPTIONS, supportedLanguages);
  const availabilityLabels = labelsFor(options.AVAILABILITY_OPTIONS, availability);

  const mappedPlatforms = platforms
    .map((slug) => SUBMISSION_PLATFORM_TO_ONTOLOGY[slug])
    .filter(Boolean);

  const { accessSurface, deliveryTechnology, isPhysical } = inferAccessFromMethods(accessMethods);
  const primaryAvailability = availability[0];
  const availabilityMeta = primaryAvailability ? AVAILABILITY_MAP[primaryAvailability] || {} : {};
  const parsedLocation = requiresLocation ? parseLocationText(locationText) : {};

  const galleryUrls = [...new Set([heroPreviewUrl, ...referenceImageUrls].filter(Boolean))];

  const priceType = pricingModels[0] ? PRICING_TO_PRICE_TYPE[pricingModels[0]] || "paid" : null;

  const availabilityStatusLabel =
    availabilityDetails?.trim() ||
    (availabilityMeta.availabilityStatus === "limited-availability" ||
    availabilityMeta.availabilityStatus === "coming-soon"
      ? availabilityLabels[0]
      : null);

  const venueModel = locationModels[0] ? LOCATION_MODEL_TO_VENUE_MODEL[locationModels[0]] || locationModels[0] : null;

  const isLBE = formats.includes("lbe") || accessMethods.includes("in-person");

  return {
    id: "submission-preview",
    title: title?.trim() || "Untitled experience",
    creatorName: creatorDisplayName?.trim() || null,
    heroImageUrl: heroPreviewUrl || "",
    realityCheckPreview: preview?.trim() || null,
    officialDescription: experienceDescription?.trim() || "",
    launchUrl: primaryExperienceLink?.trim() || null,
    primaryFormat: formats[0] || null,
    formatBadge: formatLabels[0] || null,
    formatDescriptor: durationLabels.length ? durationLabels.join(" / ") : null,
    themes,
    platforms: mappedPlatforms,
    platformLabels,
    devices: mappedPlatforms,
    deviceLabels: platformLabels,
    accessSurface,
    deliveryTechnology,
    groupSize: playerLabels,
    typicalSessionLabel: durationLabels.join(" / ") || null,
    priceType,
    priceLabel: priceDetails?.trim() || null,
    availabilityStatus: availabilityMeta.availabilityStatus || null,
    availabilityStatusLabel,
    availabilityType: availabilityMeta.availabilityType || null,
    bestFor: audienceLabels,
    languages: languageLabels,
    contentNotes: contentNotes?.trim() ? [contentNotes.trim()] : [],
    galleryUrls,
    venueModel,
    venueName: parsedLocation.venueName,
    venueAddress: parsedLocation.streetAddress,
    venueCity: parsedLocation.city,
    venueCountry: parsedLocation.country,
    isLBE,
    isPhysicalVenue: isPhysical || isLBE,
    platformNotes: platformNotes?.trim() || null,
    publishPlatform:
      accessMethods.includes("content-platform") && platformLabels[0] ? platformLabels[0] : null,
    supportedDevices: mappedPlatforms.length ? mappedPlatforms : ["browser"],
    launchInBrowser: accessMethods.includes("browser-web"),
  };
}
